import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Alert, CircularProgress, useTheme, useMediaQuery, Chip,
} from '@mui/material';
import { ArrowBack, Receipt, AttachFile, Close } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { expenseService, farmOperationService, projectService, apiClient } from '@smartgrow/shared';
import type { CreateExpenseRequest, OperationTask } from '@smartgrow/shared';

interface Category { id: string; name: string }

export const NewExpensePage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [error, setError] = useState('');
  const [selectedOpId, setSelectedOpId] = useState('');
  const [tasks, setTasks] = useState<OperationTask[]>([]);
  const [receiptFileName, setReceiptFileName] = useState('');

  const [form, setForm] = useState<CreateExpenseRequest>({
    categoryId: '',
    amount: 0,
    description: '',
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const res = await apiClient.get<Category[]>('/expenses/categories');
      return res.data;
    },
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: operationsData } = useQuery({
    queryKey: ['farm-operations', form.projectId],
    queryFn: () => farmOperationService.getByProject(form.projectId!),
    enabled: !!form.projectId,
  });

  const projects = projectsData?.content ?? [];
  const operations = operationsData?.content ?? [];

  useEffect(() => {
    if (selectedOpId) {
      farmOperationService.getTasksByOperation(selectedOpId).then(setTasks);
    } else {
      setTasks([]);
    }
  }, [selectedOpId]);

  const set = (field: keyof CreateExpenseRequest, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const mutation = useMutation({
    mutationFn: expenseService.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      navigate('/dashboard/expenses');
    },
    onError: () => setError('Failed to submit expense request.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.categoryId) { setError('Please select a category.'); return; }
    if (!form.amount || form.amount <= 0) { setError('Amount must be greater than 0.'); return; }
    if (!form.description) { setError('Description is required.'); return; }
    if (!form.projectId && !form.farmOperationId) {
      setError('Link to a Project or Farm Operation.');
      return;
    }
    setError('');
    mutation.mutate(form);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} size={isMobile ? 'small' : 'medium'}>Back</Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt color="primary" />
          <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">New Expense Request</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Expense Details</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth required label="Category" value={form.categoryId || ''}
                onChange={(e) => set('categoryId', e.target.value)}>
                {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required type="number" label="Amount (₦)"
                value={form.amount || ''}
                onChange={(e) => set('amount', Number.parseFloat(e.target.value) || 0)}
                inputProps={{ min: 1}}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth required multiline rows={3} label="Description"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="What is this expense for?"
              />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <input
                  id="receipt-upload"
                  type="file"
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setReceiptFileName(file.name);
                    const reader = new FileReader();
                    reader.onload = (ev) => set('receiptUrl', ev.target?.result as string);
                    reader.readAsDataURL(file);
                  }}
                />
                <label htmlFor="receipt-upload">
                  <Button component="span" variant="outlined" startIcon={<AttachFile />} size="small">
                    Attach Receipt
                  </Button>
                </label>
                {receiptFileName && (
                  <Chip
                    label={receiptFileName}
                    size="small"
                    sx={{ ml: 1 }}
                    onDelete={() => { setReceiptFileName(''); set('receiptUrl', undefined); }}
                    deleteIcon={<Close />}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Notes (optional)"
                value={form.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Link To (required — pick one)</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Project (optional)"
                value={form.projectId ?? ''}
                onChange={(e) => {
                  const val = e.target.value || undefined;
                  set('projectId', val);
                  setSelectedOpId('');
                  set('farmOperationId', undefined);
                  set('operationTaskId', undefined);
                }}>
                <MenuItem value="">— None —</MenuItem>
                {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Farm Operation (optional)"
                value={selectedOpId}
                disabled={!form.projectId}
                helperText={!form.projectId ? 'Select a project first' : undefined}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedOpId(val);
                  set('farmOperationId', val || undefined);
                  set('operationTaskId', undefined);
                }}>
                <MenuItem value="">— None —</MenuItem>
                {operations.map((op) => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.name || op.operationType.replace(/_/g, ' ')}
                    {op.cropVariety ? ` (${op.cropVariety})` : ''}
                    {op.animalType ? ` (${op.animalType})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {tasks.length > 0 && (
              <Grid item xs={12} md={6}>
                <TextField select fullWidth label="Task (optional)"
                  value={form.operationTaskId ?? ''}
                  onChange={(e) => set('operationTaskId', e.target.value || undefined)}>
                  <MenuItem value="">— No specific task —</MenuItem>
                  {tasks.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
                </TextField>
              </Grid>
            )}
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/dashboard/expenses')} disabled={mutation.isPending}
            fullWidth={isMobile}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" size="large" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={18} /> : undefined}
            fullWidth={isMobile}>
            {mutation.isPending ? 'Submitting...' : 'Submit Request'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
